/**
 * Sync on Load Hook
 * Syncs auto-saves between IndexedDB and Supabase when component mounts
 */

import { useEffect, useState } from 'react';
import { syncAutoSaves } from '@/services/supabaseSync';
import { isSupabaseConfigured } from '@/config/supabase';

export interface UseSyncOnLoadOptions {
  /** User ID (if null, sync is disabled) */
  userId: string | null;
  /** Product ID */
  productId: string;
  /** Side (front or back) */
  side: 'front' | 'back';
  /** Whether sync is enabled (default: true) */
  enabled?: boolean;
}

export interface UseSyncOnLoadReturn {
  /** Whether initial sync is in progress */
  isSyncing: boolean;
  /** Whether initial sync completed successfully */
  syncCompleted: boolean;
  /** Sync result stats */
  syncResult: {
    uploaded: number;
    downloaded: number;
    conflicts: number;
  } | null;
  /** Last sync error */
  error: string | null;
}

/**
 * Hook to sync auto-saves on component mount
 *
 * @example
 * ```tsx
 * const { isSyncing, syncCompleted } = useSyncOnLoad({
 *   userId: user?.id ?? null,
 *   productId: product.id,
 *   side: activeSide,
 * });
 * ```
 */
export function useSyncOnLoad(options: UseSyncOnLoadOptions): UseSyncOnLoadReturn {
  const { userId, productId, side, enabled = true } = options;

  const [isSyncing, setIsSyncing] = useState(false);
  const [syncCompleted, setSyncCompleted] = useState(false);
  const [syncResult, setSyncResult] = useState<{
    uploaded: number;
    downloaded: number;
    conflicts: number;
  } | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Don't sync if disabled, no user, or Supabase not configured
    if (!enabled || !userId || !isSupabaseConfigured()) {
      return;
    }

    let mounted = true;

    async function performSync() {
      if (!userId) return; // Type guard: userId is string here

      setIsSyncing(true);
      setError(null);

      try {
        const result = await syncAutoSaves(userId, productId, side);

        if (!mounted) return;

        setSyncResult(result);
        setSyncCompleted(true);

        // Log sync result
        if (result.uploaded > 0 || result.downloaded > 0) {
          console.warn(
            `[useSyncOnLoad] Synced auto-saves: ↑${result.uploaded} ↓${result.downloaded} (${result.conflicts} conflicts resolved)`
          );
        }
      } catch (err) {
        if (!mounted) return;

        const errorMessage = err instanceof Error ? err.message : 'Sync failed';
        console.error('[useSyncOnLoad] Sync failed:', err);
        setError(errorMessage);
      } finally {
        if (mounted) {
          setIsSyncing(false);
        }
      }
    }

    performSync();

    return () => {
      mounted = false;
    };
  }, [userId, productId, side, enabled]);

  return {
    isSyncing,
    syncCompleted,
    syncResult,
    error,
  };
}
